import {
  AnnotationPoint,
  BlurAnnotation,
  ImageAnnotationDocument,
  ImageRotation,
  LegacyMarkerAnnotation,
  MARKER_DEFINITIONS,
  MEASUREMENT_UNIT_DEFINITIONS,
  MarkerDefinition,
  MarkerAnnotation,
  MarkerSet,
  MeasurementType,
  MeasurementUnit,
  MeasurementUnitDefinition,
} from './image-annotation-editor.types';

export interface FloatingPalettePosition {
  x: number;
  y: number;
}

export interface FloatingPaletteSize {
  width: number;
  height: number;
}

export interface FloatingAnchorRect extends FloatingPalettePosition, FloatingPaletteSize {}

export function getRotatedImageSize(
  image: FloatingPaletteSize,
  rotation: ImageRotation,
): FloatingPaletteSize {
  return rotation === 90 || rotation === 270
    ? { width: image.height, height: image.width }
    : { ...image };
}

export function mapRotatedPointToOriginal(
  point: AnnotationPoint,
  image: FloatingPaletteSize,
  rotation: ImageRotation,
): AnnotationPoint {
  if (rotation === 90) return { x: point.y, y: image.height - point.x };
  if (rotation === 180) return { x: image.width - point.x, y: image.height - point.y };
  if (rotation === 270) return { x: image.width - point.y, y: point.x };
  return { x: point.x, y: point.y };
}

export function rotateImagePoint(
  point: AnnotationPoint,
  image: FloatingPaletteSize,
  rotation: ImageRotation,
): AnnotationPoint {
  const pressure = point.pressure === undefined ? {} : { pressure: point.pressure };
  if (rotation === 90) return { x: image.height - point.y, y: point.x, ...pressure };
  if (rotation === 180) return { x: image.width - point.x, y: image.height - point.y, ...pressure };
  if (rotation === 270) return { x: point.y, y: image.width - point.x, ...pressure };
  return { ...point };
}

export function combineImageRotations(first: ImageRotation, second: ImageRotation): ImageRotation {
  return ((first + second) % 360) as ImageRotation;
}

export function rebaseAnnotationDocumentRotation(data: ImageAnnotationDocument): ImageAnnotationDocument {
  const document = normalizeAnnotationDocument(data);
  const rotation = document.rotation ?? 0;
  if (rotation === 0) return document;
  const image = document.baseImage;
  const rotatedSize = getRotatedImageSize(image, rotation);
  const annotations = document.annotations.map(annotation => {
    if (annotation.type === 'stroke') {
      return { ...annotation, points: annotation.points.map(point => rotateImagePoint(point, image, rotation)) };
    }
    if (annotation.type === 'blur') {
      const corners = [
        { x: annotation.x, y: annotation.y },
        { x: annotation.x + annotation.width, y: annotation.y },
        { x: annotation.x, y: annotation.y + annotation.height },
        { x: annotation.x + annotation.width, y: annotation.y + annotation.height },
      ].map(point => rotateImagePoint(point, image, rotation));
      const xs = corners.map(point => point.x);
      const ys = corners.map(point => point.y);
      const x = Math.min(...xs);
      const y = Math.min(...ys);
      return {
        ...annotation,
        x,
        y,
        width: Math.max(...xs) - x,
        height: Math.max(...ys) - y,
      };
    }
    if (annotation.type === 'measurement') {
      const orientation = combineImageRotations(annotation.orientation ?? 0, rotation);
      const rotated = {
        ...annotation,
        point: rotateImagePoint(annotation.point, image, rotation),
        labelPosition: rotateImagePoint(annotation.labelPosition, image, rotation),
      };
      if (orientation === 0) delete rotated.orientation;
      else rotated.orientation = orientation;
      return rotated;
    }
    if (annotation.type === 'marker') {
      const marker = normalizeMarkerAnnotation(annotation, image.width, image.height);
      const orientation = combineImageRotations(marker.orientation ?? 0, rotation);
      const rotated = {
        ...marker,
        point: rotateImagePoint(marker.point, image, rotation),
        labelPosition: rotateImagePoint(marker.labelPosition, image, rotation),
      };
      if (orientation === 0) delete rotated.orientation;
      else rotated.orientation = orientation;
      return rotated;
    }
    return { ...annotation, point: rotateImagePoint(annotation.point, image, rotation) };
  });
  return {
    version: 1,
    baseImage: rotatedSize,
    annotations,
  };
}

export type LabelConnectionShape = 'rectangle' | 'ellipse';

export function getLabelConnectionPoint(
  point: AnnotationPoint,
  bounds: FloatingAnchorRect,
  shape: LabelConnectionShape = 'rectangle',
): AnnotationPoint {
  const centerX = bounds.x + bounds.width / 2;
  const centerY = bounds.y + bounds.height / 2;
  const candidates = shape === 'ellipse'
    ? Array.from({ length: 12 }, (_, index) => {
      const angle = -Math.PI * 2 / 3 + index * Math.PI / 6;
      return {
        x: centerX + Math.cos(angle) * bounds.width / 2,
        y: centerY + Math.sin(angle) * bounds.height / 2,
      };
    })
    : [
      { x: bounds.x + bounds.width * 0.25, y: bounds.y },
      { x: centerX, y: bounds.y },
      { x: bounds.x + bounds.width * 0.75, y: bounds.y },
      { x: bounds.x + bounds.width, y: bounds.y + bounds.height * 0.25 },
      { x: bounds.x + bounds.width, y: centerY },
      { x: bounds.x + bounds.width, y: bounds.y + bounds.height * 0.75 },
      { x: bounds.x + bounds.width * 0.75, y: bounds.y + bounds.height },
      { x: centerX, y: bounds.y + bounds.height },
      { x: bounds.x + bounds.width * 0.25, y: bounds.y + bounds.height },
      { x: bounds.x, y: bounds.y + bounds.height * 0.75 },
      { x: bounds.x, y: centerY },
      { x: bounds.x, y: bounds.y + bounds.height * 0.25 },
    ];
  return candidates.reduce((nearest, candidate) => (
    Math.hypot(candidate.x - point.x, candidate.y - point.y)
      < Math.hypot(nearest.x - point.x, nearest.y - point.y)
      ? candidate
      : nearest
  ));
}

export function getAdjacentFloatingPanelPosition(
  viewport: FloatingPaletteSize,
  anchor: FloatingAnchorRect,
  panel: FloatingPaletteSize,
  gap = 8,
): FloatingPalettePosition {
  const spaceAbove = anchor.y - gap;
  const spaceBelow = viewport.height - anchor.y - anchor.height - gap;
  const showAbove = spaceAbove >= panel.height || spaceAbove >= spaceBelow;
  const preferredX = anchor.x + (anchor.width - panel.width) / 2;
  const preferredY = showAbove
    ? anchor.y - panel.height - gap
    : anchor.y + anchor.height + gap;
  return {
    x: Math.max(gap, Math.min(viewport.width - panel.width - gap, preferredX)),
    y: Math.max(gap, Math.min(viewport.height - panel.height - gap, preferredY)),
  };
}

export function clampImageViewportPosition(
  position: FloatingPalettePosition,
  viewport: FloatingPaletteSize,
  image: FloatingPaletteSize,
  scale: number,
): FloatingPalettePosition {
  const scaledWidth = image.width * scale;
  const scaledHeight = image.height * scale;
  return {
    x: scaledWidth <= viewport.width
      ? (viewport.width - scaledWidth) / 2
      : Math.max(viewport.width - scaledWidth, Math.min(0, position.x)),
    y: scaledHeight <= viewport.height
      ? (viewport.height - scaledHeight) / 2
      : Math.max(viewport.height - scaledHeight, Math.min(0, position.y)),
  };
}

export function clampFloatingPalettePosition(
  position: FloatingPalettePosition,
  container: FloatingPaletteSize,
  palette: FloatingPaletteSize,
): FloatingPalettePosition {
  return {
    x: Math.max(0, Math.min(Math.max(0, container.width - palette.width), position.x)),
    y: Math.max(0, Math.min(Math.max(0, container.height - palette.height), position.y)),
  };
}

/** Keeps an existing blur rectangle inside the source image while it is moved. */
export function clampBlurPosition(
  position: FloatingPalettePosition,
  blur: Pick<BlurAnnotation, 'width' | 'height'>,
  image: FloatingPaletteSize,
): FloatingPalettePosition {
  return {
    x: Math.max(0, Math.min(Math.max(0, image.width - blur.width), position.x)),
    y: Math.max(0, Math.min(Math.max(0, image.height - blur.height), position.y)),
  };
}

export function getInitialFloatingPalettePosition(
  container: FloatingPaletteSize,
  palette: FloatingPaletteSize,
  bottomMargin = 10,
): FloatingPalettePosition {
  return clampFloatingPalettePosition({
    x: (container.width - palette.width) / 2,
    y: container.height - palette.height - bottomMargin,
  }, container, palette);
}

export function getAutomaticMarkerLabelPosition(
  point: AnnotationPoint,
  imageWidth: number,
  imageHeight: number,
  occupiedLabelPositions: readonly AnnotationPoint[] = [],
): AnnotationPoint {
  const shortSide = Math.max(1, Math.min(imageWidth, imageHeight));
  const distance = Math.max(56, shortSide * 0.075);
  const labelRadius = Math.max(38, shortSide * 0.042) * 0.72;
  const margin = labelRadius + Math.max(8, shortSide * 0.008);
  const directionX = point.x <= imageWidth / 2 ? 1 : -1;
  const directionY = point.y <= imageHeight * 0.25 ? 1 : -1;
  const preferredAngle = Math.atan2(directionY, directionX);
  const candidateAngles = [
    preferredAngle,
    preferredAngle + Math.PI / 2,
    preferredAngle - Math.PI / 2,
    preferredAngle + Math.PI,
    0,
    Math.PI / 2,
    Math.PI,
    -Math.PI / 2,
  ];
  const candidates = candidateAngles.map(angle => ({
    x: Math.max(margin, Math.min(imageWidth - margin, point.x + Math.cos(angle) * distance)),
    y: Math.max(margin, Math.min(imageHeight - margin, point.y + Math.sin(angle) * distance)),
  }));
  if (!occupiedLabelPositions.length) return candidates[0];
  return candidates.reduce((best, candidate) => {
    const clearance = Math.min(...occupiedLabelPositions.map(occupied => (
      Math.hypot(candidate.x - occupied.x, candidate.y - occupied.y)
    )));
    return clearance > best.clearance ? { candidate, clearance } : best;
  }, { candidate: candidates[0], clearance: -1 }).candidate;
}

export function normalizeMarkerAnnotation(
  marker: MarkerAnnotation | LegacyMarkerAnnotation,
  imageWidth: number,
  imageHeight: number,
): MarkerAnnotation {
  const point = marker.point ?? marker.position;
  const normalized: MarkerAnnotation = {
    id: marker.id,
    type: 'marker',
    markerSet: marker.markerSet,
    value: marker.value,
    text: marker.text,
    color: marker.color,
    point: { ...point },
    labelPosition: marker.labelPosition
      ? { ...marker.labelPosition }
      : getAutomaticMarkerLabelPosition(point, imageWidth, imageHeight),
  };
  if (marker.orientation) normalized.orientation = marker.orientation;
  return normalized;
}

export function normalizeAnnotationDocument(data: ImageAnnotationDocument): ImageAnnotationDocument {
  const normalized: ImageAnnotationDocument = {
    version: data.version,
    baseImage: { ...data.baseImage },
    annotations: data.annotations.map(annotation => annotation.type === 'marker'
      ? normalizeMarkerAnnotation(annotation, data.baseImage.width, data.baseImage.height)
      : JSON.parse(JSON.stringify(annotation))),
  };
  const rotation = data.rotation ?? 0;
  if ([90, 180, 270].includes(rotation)) normalized.rotation = rotation as ImageRotation;
  return normalized;
}

export function getMeasurementUnitDefinition(unit: MeasurementUnit): MeasurementUnitDefinition {
  const definition = MEASUREMENT_UNIT_DEFINITIONS.find(item => item.id === unit);
  if (!definition) throw new Error(`Unknown measurement unit: ${unit}`);
  return definition;
}

export function getMeasurementUnitGroups(): readonly {
  category: MeasurementType;
  label: string;
  units: readonly MeasurementUnitDefinition[];
}[] {
  const categories: MeasurementType[] = [
    'voltage',
    'resistance',
    'current',
    'capacitance',
    'inductance',
    'frequency',
    'temperature',
  ];
  return categories.flatMap(category => {
    const units = MEASUREMENT_UNIT_DEFINITIONS.filter(item => item.category === category);
    return units.length ? [{ category, label: units[0].categoryLabel, units }] : [];
  });
}

export function getMarkerDefinitions(markerSet: MarkerSet): readonly MarkerDefinition[] {
  return MARKER_DEFINITIONS.filter(marker => marker.markerSet === markerSet);
}

export interface NextMarkerPlacement {
  marker: MarkerDefinition;
  color: string;
}

export function getNextMarkerPlacement(
  markerSet: MarkerSet,
  preferredColor: string,
  paletteColors: readonly string[],
  annotations: readonly ImageAnnotationDocument['annotations'][number][],
): NextMarkerPlacement | undefined {
  const normalizeColor = (color: string): string => color.trim().toLowerCase();
  const preferredIndex = paletteColors.findIndex(color => normalizeColor(color) === normalizeColor(preferredColor));
  const orderedColors = preferredIndex >= 0
    ? [...paletteColors.slice(preferredIndex), ...paletteColors.slice(0, preferredIndex)]
    : [preferredColor, ...paletteColors.filter(color => normalizeColor(color) !== normalizeColor(preferredColor))];
  const definitions = getMarkerDefinitions(markerSet);
  for (const color of orderedColors) {
    const usedValues = new Set(annotations.flatMap(annotation => (
      annotation.type === 'marker'
        && annotation.markerSet === markerSet
        && normalizeColor(annotation.color) === normalizeColor(color)
        ? [annotation.value]
        : []
    )));
    const marker = definitions.find(definition => !usedValues.has(definition.value));
    if (marker) return { marker, color };
  }
  return undefined;
}

export function getNextMarkerDefinition(current: MarkerDefinition): MarkerDefinition {
  const definitions = getMarkerDefinitions(current.markerSet);
  const index = definitions.findIndex(item => item.value === current.value);
  return index >= 0 && index < definitions.length - 1 ? definitions[index + 1] : current;
}

export function findMarkerDefinition(markerSet: MarkerSet, value: number | string): MarkerDefinition | undefined {
  return MARKER_DEFINITIONS.find(marker => marker.markerSet === markerSet && marker.value === value);
}
